import { Button, TextField } from "@mui/material";
import { useState } from "react";
import { handleCreateNewCompany } from "../../../api/mosaic/accounts";
import { useToast } from "../../shared/Toast";
import { handleCreatePermissionsApi } from "../../../api/mosaic/roles";
import PermissionsTable, { getInitialPermissions, IPermissionItems } from "./PermissionsTable";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";

interface IAddCompanyModal {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDoneSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddCompanyModal = ({
  open,
  setOpen,
  setIsDoneSaving,
}: IAddCompanyModal) => {
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [permissions, setPermissions] = useState<Record<string, IPermissionItems>>(getInitialPermissions());

  const handleClose = () => {
    setActiveStep(0);
    setFormData("");
    setPermissions(getInitialPermissions());
    setOpen(false);
  };

  const handlePermissionToggle = (_e: React.ChangeEvent<HTMLInputElement>, permissionName: string) => {
    setPermissions((prev) => ({
      ...prev,
      [permissionName]: { ...prev[permissionName], enabled: !prev[permissionName].enabled },
    }));
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const result = await handleCreateNewCompany(formData);
      const newCompany = result.data?.data;

      if (newCompany?.id) {
        const enabledPermissions = Object.values(permissions).filter((p) => p.enabled);
        await Promise.all(
          enabledPermissions.map((p) =>
            handleCreatePermissionsApi({ name: p.name, companyId: newCompany.id })
          )
        );
      }

      setIsLoading(false);
      setIsDoneSaving((prev) => !prev);
      handleClose();
      showSuccess("Company created successfully");
    } catch (err) {
      setIsLoading(false);
      showError("Error creating company");
      console.error(err);
    }
  };

  return (
    <ModalWrapper
      open={open}
      onClose={handleClose}
      title={activeStep === 0 ? "Add new company" : "Set Permissions"}
      maxWidth={activeStep === 1 ? "md" : "sm"}
      actions={
        activeStep === 0 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            disabled={!formData.trim()}
            sx={modalSubmitButtonSx}
          >
            Next
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={() => setActiveStep(0)} disabled={isLoading} sx={modalCancelButtonSx}>
              Back
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={modalSubmitButtonSx}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </>
        )
      }
    >
      {activeStep === 0 && (
        <TextField
          fullWidth
          label="Company Name"
          variant="outlined"
          value={formData}
          onChange={(e) => setFormData(e.target.value)}
          inputProps={{ maxLength: 100 }}
        />
      )}

      {activeStep === 1 && (
        <PermissionsTable permissions={permissions} onToggle={handlePermissionToggle} />
      )}
    </ModalWrapper>
  );
};

export default AddCompanyModal;
